/**
 * Chunked matrix table builder for Inventory Health (and similar) views.
 * Consumes parquet in matrix shape (one row per (segmentation, measure), date columns)
 * from the chunked provider and produces the same virtual table shape as createInventoryHealthMatrix.
 */

import * as arrow from "apache-arrow";
import React, { useMemo, useCallback, useContext, useRef, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DatePivotParams } from "../entities/date_pivot_params";
import dayjs from "./dayjs";
import { isHistoricalDate } from "./mui_table_utils";
import { getOrderedSegments } from "./segment_order";
import { VersionedDataContext } from "../contexts/versioned_data/context";
import {
  getPeriodLabel,
  getSupplyCellBackgroundColor,
  getWOSMeasureKey,
} from "./supply_color_coding";
import type { ChunkedParsedParquetData, FullParsedParquetData } from "./parquet";
import { openPoCellPopover } from "../components/po_cell_popover/store";
import { AtomicLogo } from "../assets/AtomicLogo";

// Snake_case measure names (three-forecast view) render with spaces, matching
// the legacy forecast tracking's lowercase labels; human-named measures pass
// through untouched. Display-only — matching still keys on the raw name.
function formatMeasureLabel(measure: unknown): string {
  return String(measure ?? "").replaceAll("_", " ");
}

const MEASURES_WITH_TOTALS = [
  "Purchase Order To Place",
  "Forecasted Sales",
  "Purchase Order Arrivals",
  "Unfulfilled Demand",
  "Transfer Order Arrivals",
  "forecast",
  "orders",
  "remainder",
];

const ASC_DEFAULT_MEASURES = [
  "Starting weeks of supply",
  "Starting months of supply",
  "Starting days of supply",
  "Ending weeks of supply",
  "Ending months of supply",
  "Ending days of supply",
  "In stock rate",
];

const MATRIX_FIXED_COLUMNS = new Set([
  "segmentation",
  "measure",
  "measure_rank",
  "segmentation_rank",
  "TOTAL",
]);

export interface ChunkedMatrixRowData {
  type: "group" | "detail";
  groupKey?: string;
  measure?: string;
  dateValues: Record<string, unknown>;
  /** Per-date freeze dates (three-forecast view): dateKey -> YYYY-MM-DD. */
  frozenDates?: Record<string, string>;
  segmentationRank: number;
  measureRank?: number;
  isExpanded?: boolean;
  groupSize?: number;
  segmentation?: string;
  /** Set by getRow for WOS target lookup (block index). */
  segIdx?: number;
  /** 0-based position of a detail row within its group (zebra striping). */
  detailIndex?: number;
  [key: string]: unknown;
}

function isLoadingRow(row: Record<string, unknown>): boolean {
  return (row?.__chunkedLoading as boolean) === true;
}

/** Format matrix cell value by measure (WOS 1 decimal, in stock %, etc.). */
function formatMatrixCellValue(
  value: unknown,
  measureName: string | undefined
): string {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  const m = (measureName ?? "").toLowerCase();
  if (
    m.includes("in stock rate") ||
    m.includes("pct ordered") ||
    m.includes("forecast_accuracy") ||
    m.includes("forecast_bias")
  ) {
    return `${(num * 100).toFixed(1)}%`;
  }
  if (
    m.includes("weeks of supply") ||
    m.includes("months of supply") ||
    m.includes("days of supply")
  ) {
    return num.toFixed(1);
  }
  if (
    m.includes("forecasted sales") ||
    m.includes("purchase order") ||
    m.includes("unfulfilled demand")
  ) {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

// Zebra shading for expanded detail rows (readability pass): alternating
// white / near-white so the eye can track a single measure straight across
// all date columns; replaces the old flat action.hover fill.
function detailZebraColor(row: ChunkedMatrixRowData): string {
  return (row.detailIndex ?? 0) % 2 === 1 ? "#FBFCFA" : "#FFFFFF";
}

// Signed measures render as colored text (red for shortfall) instead of
// adding more background fills on top of the WOS tints.
const SIGNED_SHORTFALL_MEASURES = new Set([
  "transfer order needed",
  "transfer order to place",
  "unfulfilled demand",
]);

function getDateColumnKeys(columns: arrow.Field<any>[]): string[] {
  return columns
    .filter(
      (c) =>
        c.name &&
        !MATRIX_FIXED_COLUMNS.has(c.name) &&
        // per-cell metadata columns (freeze dates), not date columns
        !c.name.endsWith("__frozen_date")
    )
    .map((c) => c.name);
}

/** Column names that are segment dimensions (e.g. sku, location) when transform has no single "segmentation" column. */
function getSegmentDimensionColumnNames(columns: arrow.Field<any>[]): string[] {
  return columns
    .filter(
      (c) =>
        c.name &&
        !MATRIX_FIXED_COLUMNS.has(c.name) &&
        c.name !== "TOTAL" &&
        !c.name.endsWith("__frozen_date") &&
        isNaN(Number(c.name))
    )
    .map((c) => c.name);
}

/**
 * Infer M (measures per segmentation) by reading rows until the segmentation
 * identity changes.  We check the "segmentation" column first (a single
 * composite key); if it is absent we fall back to segmentation_rank — but
 * segmentation_rank alone is unreliable because tied forecasts produce
 * identical ranks for different segmentations.
 */
function inferMeasuresPerSegmentation(
  rowCount: number,
  getRow: (i: number) => Record<string, unknown>,
  maxLook: number = 500
): number {
  const r0 = getRow(0);
  if (isLoadingRow(r0)) return 0;
  const firstSeg = String(r0.segmentation ?? "");
  const firstRank = Number(r0.segmentation_rank ?? 0);
  for (let i = 1; i < Math.min(rowCount, maxLook); i++) {
    const r = getRow(i);
    if (isLoadingRow(r)) return 0;
    // Primary check: segmentation column (unique per group).
    if (firstSeg && String(r.segmentation ?? "") !== firstSeg) return i;
    // Fallback: rank change (still useful when segmentation column is absent).
    if (!firstSeg && Number(r.segmentation_rank ?? 0) !== firstRank) return i;
  }
  return Math.min(rowCount, maxLook);
}

export type GetChunkedMatrixTableDataParams = {
  chunkedData: ChunkedParsedParquetData;
  viewParams: DatePivotParams;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  theme: unknown;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  /**
   * Optional callback that fetches sort-key data for a given measure from the
   * backend.  Returns a parsed parquet (one row per segmentation for that
   * measure) which the hook uses to build the sorted display order without
   * loading all chunks into browser memory.
   */
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  /** Measure names that render as indented sub-rows (breakdown rows). */
  breakdownMeasures?: string[];
  /** Additional measure names that should show a TOTAL column. */
  additionalMeasuresWithTotals?: string[];
  /**
   * When true, grey historical (past/current) month columns and drop the
   * always-on detail-row shading — matching the demand/supply no-perspective
   * views. Used by forecast tracking; inventory-health views leave it unset.
   */
  shadeHistory?: boolean;
  /**
   * When true, detail/breakdown label cells render without the action.hover
   * fill so only date columns carry shading. Unset = legacy grey labels.
   */
  plainDetailLabels?: boolean;
  /**
   * When true, "open" / "simulated" PO-arrival breakdown cells with value > 0
   * become clickable and open the PoCellPopover (Inventory Health only).
   */
  enablePoCellPopover?: boolean;
  /**
   * When true and the visible date range spans the active run's as-of date,
   * render the ❯ ACTUALS / FORECAST strip: actuals columns tinted #F4F6F8,
   * a dashed brand-blue boundary at the as-of split, and a chevron that
   * collapses the actuals columns into one summary column.
   */
  showActualsForecastStrip?: boolean;
};

export type ChunkedMatrixTableDataResult = {
  columns: {
    key: string;
    label?: string;
    width?: number;
    isExpandColumn?: boolean;
    sortable?: boolean;
    sticky?: boolean;
    onSort?: (e?: React.MouseEvent<HTMLTableCellElement>) => void;
    headerType?: arrow.DataType;
    renderCell?: (row: ChunkedMatrixRowData, rowIndex: number) => React.ReactNode;
    boundaryRight?: boolean;
  }[];
  getRow: (rowIndex: number) => ChunkedMatrixRowData;
  rowCount: number;
  columnTotals?: Record<string, number>;
  isReady: boolean;
  /** True when all sort keys have been loaded and the table is displayed in sorted order. */
  isSortReady: boolean;
  /** ❯ ACTUALS / FORECAST strip config for MUIFlatTable (when enabled). */
  headerBand?: import("../components/universal_mui_table/MUIFlatTable").MUIFlatTableHeaderBand;
};

/**
 * Hook: build table data (columns, getRow, rowCount) for chunked matrix parquet.
 * Matrix parquet: one row per (segmentation, measure), sorted by (segmentation_rank, measure_rank).
 * Returns isReady when M and totalSegmentations are known (no preload of all keys).
 */
export function useChunkedMatrixTableData({
  chunkedData,
  viewParams,
  expandedGroups,
  setExpandedGroups,
  theme,
  sortConfig,
  setSortConfig,
  fetchSortKeys,
  breakdownMeasures,
  additionalMeasuresWithTotals,
  shadeHistory,
  plainDetailLabels,
  enablePoCellPopover,
  showActualsForecastStrip,
}: GetChunkedMatrixTableDataParams): ChunkedMatrixTableDataResult {
  const { rowCount: parquetRowCount, getRow: getParquetRow, columns } = chunkedData;
  const dateColumnKeys = useMemo(() => getDateColumnKeys(columns), [columns]);

  // Actuals/forecast split, keyed on the ACTIVE RUN's as-of date — not
  // today's wall clock — so an old run's actuals boundary stays where the
  // data says it is.
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const runAsOf = (mainRun ?? lastSuccessfulRun)?.createdAt ?? null;
  const stripEnabled = showActualsForecastStrip === true && runAsOf != null;
  const [actualsCollapsed, setActualsCollapsed] = useState(false);
  const segmentDimensionColumns = useMemo(
    () => getSegmentDimensionColumnNames(columns),
    [columns]
  );

  /**
   * On-screen order of the segment label columns, driven by the chip order in
   * the "Segment by" control (viewParams.segments). The parquet order from
   * getSegmentDimensionColumnNames is the fallback and is deliberately left
   * untouched for group-key composition in getSegKey — reordering the display
   * must not change group identity, or every expanded group would collapse.
   * Columns absent from the chip list keep their parquet order, after the
   * ones that are listed.
   */
  const segmentsOrderKey = JSON.stringify(
    getOrderedSegments(
      (viewParams as { segments?: string[] })?.segments,
      (viewParams as { segmentOrder?: string[] })?.segmentOrder
    )
  );
  const orderedSegmentColumns = useMemo(() => {
    const requested: string[] = JSON.parse(segmentsOrderKey);
    if (requested.length === 0) return segmentDimensionColumns;
    const rank = new Map(requested.map((s, i) => [s, i]));
    return [...segmentDimensionColumns].sort((a, b) => {
      const ra = rank.get(a) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return (
        segmentDimensionColumns.indexOf(a) - segmentDimensionColumns.indexOf(b)
      );
    });
  }, [segmentDimensionColumns, segmentsOrderKey]);
  const hasTotalColumn = useMemo(
    () => columns.some((c) => c.name === "TOTAL"),
    [columns]
  );
  const allMeasuresWithTotals = additionalMeasuresWithTotals
    ? [...MEASURES_WITH_TOTALS, ...additionalMeasuresWithTotals]
    : MEASURES_WITH_TOTALS;
  const shouldShowTotals = allMeasuresWithTotals.some(
    (m) => viewParams.measure?.toLowerCase() === m.toLowerCase()
  );

  const M = useMemo(() => {
    return inferMeasuresPerSegmentation(parquetRowCount, getParquetRow);
  }, [parquetRowCount, getParquetRow]);

  const totalSegmentations = M > 0 ? Math.floor(parquetRowCount / M) : 0;

  useEffect(() => {
    if (M > 0 && totalSegmentations > 0) {
      console.log("[matrix transform debug] Inferred matrix shape:", {
        parquetRowCount,
        M,
        totalSegmentations,
        expected: "parquetRowCount === totalSegmentations * M",
        match: parquetRowCount === totalSegmentations * M,
      });
    }
  }, [M, totalSegmentations, parquetRowCount]);

  // No preload of all segmentation keys (would load hundreds of chunks). Keys are
  // resolved lazily via getSegKey(segIdx) when building rows.

  const virtualRowCount = useMemo(() => {
    const expandedCount = expandedGroups.has("*") ? totalSegmentations : expandedGroups.size;
    return totalSegmentations + expandedCount * M;
  }, [totalSegmentations, expandedGroups, M]);

  const blockCache = useMemo(() => new Map<number, Record<string, unknown>[]>(), []);
  const segKeyCache = useMemo(() => new Map<number, string>(), []);

  const matrixDebugLogged = useRef(false);
  const matrixDebugFirstRowLogged = useRef(false);
  const matrixDebugMeasuresLogged = useRef(false);

  const getSegKey = useCallback(
    (segIdx: number): string => {
      let key = segKeyCache.get(segIdx);
      if (key !== undefined) return key;
      const row = getParquetRow(segIdx * M);
      if (isLoadingRow(row)) {
        key = "";
      } else {
        const r = row as Record<string, unknown>;
        const raw =
          r.segmentation ??
          r.segment ??
          r.segmentation_key ??
          r["Segment"];
        if (raw != null && String(raw).trim() !== "") {
          key = String(raw).trim();
        } else if (segmentDimensionColumns.length > 0) {
          key = segmentDimensionColumns
            .map((col) => r[col])
            .filter((v) => v != null && v !== "")
            .join(" | ");
        } else {
          key = "";
        }
      }
      // Only cache when row is loaded; otherwise we'd keep showing empty after chunk loads
      if (!isLoadingRow(row)) {
        segKeyCache.set(segIdx, key);
      }

      if (segIdx === 0 && !matrixDebugLogged.current) {
        matrixDebugLogged.current = true;
        const r = getParquetRow(0) as Record<string, unknown>;
        console.log("[matrix transform debug] getSegKey(0) from parquet row:", {
          rowKeys: isLoadingRow(r) ? ["(loading)"] : Object.keys(r),
          segmentation: r.segmentation,
          segmentDimensionColumns,
          builtFromDimensions: segmentDimensionColumns.map((c) => r[c]),
          resolvedKey: key,
          usedPlaceholder: key === "" ? "yes (will use __seg_0 for expand)" : "no",
        });
      }
      return key;
    },
    [getParquetRow, M, segKeyCache, segmentDimensionColumns]
  );

  const getBlock = useCallback(
    (segIdx: number): Record<string, unknown>[] => {
      let block = blockCache.get(segIdx);
      if (block) return block;
      block = [];
      for (let k = 0; k < M; k++) {
        block.push(getParquetRow(segIdx * M + k));
      }
      // Only cache when the block is fully loaded; otherwise when the chunk
      // loads later we would keep returning the same incomplete block.
      if (!block.some(isLoadingRow)) {
        blockCache.set(segIdx, block);
      }
      return block;
    },
    [getParquetRow, M, blockCache]
  );

  const selectedMeasureRank = useMemo(() => {
    if (M === 0) return 0;
    const firstBlock = getParquetRow(0);
    if (isLoadingRow(firstBlock)) return 0;
    for (let k = 0; k < M; k++) {
      const r = getParquetRow(k);
      if (isLoadingRow(r)) return 0;
      if (
        String(r.measure ?? "").toLowerCase() ===
        viewParams.measure?.toLowerCase()
      )
        return Number(r.measure_rank ?? k);
    }
    return 0;
  }, [M, getParquetRow, viewParams.measure]);

  const measureRanksInOrder = useMemo(() => {
    const ranks: number[] = [];
    for (let k = 0; k < M; k++) {
      const r = getParquetRow(k);
      if (isLoadingRow(r)) return [];
      ranks.push(Number(r.measure_rank ?? k));
    }
    ranks.sort((a, b) => a - b);
    return ranks;
  }, [M, getParquetRow]);

  const allDates = useMemo(
    () =>
      dateColumnKeys
        .map((k) => {
          const n = Number(k);
          return isNaN(n) ? null : dayjs.utc(n);
        })
        .filter((d): d is dayjs.Dayjs => d != null)
        .sort((a, b) => a.valueOf() - b.valueOf()),
    [dateColumnKeys]
  );

  // ---------------------------------------------------------------------------
  // Default sort initialization (mirrors main branch behavior).
  // When sortConfig is empty and we have date columns, auto-set initial sort.
  // ---------------------------------------------------------------------------
  const [defaultSortInitialized, setDefaultSortInitialized] = useState(false);
  useEffect(() => {
    if (defaultSortInitialized) return;
    if (M === 0 || totalSegmentations === 0) return;

    if (allDates.length === 0) {
      setDefaultSortInitialized(true);
      return;
    }

    if (sortConfig.length === 0) {
      const isSupplyMeasure = ASC_DEFAULT_MEASURES.some(
        (m) => viewParams.measure?.toLowerCase() === m.toLowerCase()
      );
      const defaultDirection: "asc" | "desc" = isSupplyMeasure ? "asc" : "desc";
      const initialSort = allDates.map((date, index) => ({
        key: `${date.valueOf()}`,
        direction: defaultDirection,
        index,
      }));
      setDefaultSortInitialized(true);
      setSortConfig(initialSort);
    } else {
      setDefaultSortInitialized(true);
    }
  }, [allDates, M, totalSegmentations, sortConfig.length, viewParams.measure, setSortConfig, defaultSortInitialized]);

  const handleDateColumnSort = useCallback(
    (columnKey: string, shiftKey: boolean) => {
      if (!setSortConfig) return;

      const isSupplyMeasure = ASC_DEFAULT_MEASURES.some(
        (m) => viewParams.measure?.toLowerCase() === m.toLowerCase()
      );
      const defaultDir: "asc" | "desc" = isSupplyMeasure ? "asc" : "desc";

      if (!shiftKey) {
        // Single click: sort by ONLY this column.
        const existing = sortConfig.find((s) => s.key === columnKey);
        if (existing && sortConfig.length === 1) {
          // Already the sole sort column — cycle: default → opposite → remove
          if (existing.direction === defaultDir) {
            setSortConfig([
              { key: columnKey, direction: defaultDir === "asc" ? "desc" : "asc", index: 0 },
            ]);
          } else {
            setSortConfig([]);
          }
        } else {
          setSortConfig([{ key: columnKey, direction: defaultDir, index: 0 }]);
        }
      } else {
        // Shift+click: multi-column sort — add or toggle within existing config.
        const existingIdx = sortConfig.findIndex((s) => s.key === columnKey);
        if (existingIdx >= 0) {
          const existing = sortConfig[existingIdx];
          if (existing.direction === defaultDir) {
            setSortConfig(
              sortConfig.map((s, i) =>
                i === existingIdx
                  ? { ...s, direction: (defaultDir === "asc" ? "desc" : "asc") as "asc" | "desc" }
                  : s
              )
            );
          } else {
            // Remove this column from multi-sort
            const remaining = sortConfig.filter((_, i) => i !== existingIdx);
            setSortConfig(remaining.map((s, i) => ({ ...s, index: i })));
          }
        } else {
          // Add as lowest-priority sort key
          const filtered = sortConfig.filter((s) => s.key !== "TOTAL");
          setSortConfig([
            ...filtered,
            { key: columnKey, direction: defaultDir, index: filtered.length },
          ]);
        }
      }
    },
    [setSortConfig, sortConfig, viewParams.measure]
  );

  const handleSegmentColumnSort = useCallback(
    (columnKey: string, shiftKey: boolean) => {
      if (!setSortConfig) return;
      if (!shiftKey) {
        const existing = sortConfig.find((s) => s.key === columnKey);
        if (existing && sortConfig.length === 1) {
          if (existing.direction === "asc") {
            setSortConfig([{ key: columnKey, direction: "desc", index: 0 }]);
          } else {
            setSortConfig([]);
          }
        } else {
          setSortConfig([{ key: columnKey, direction: "asc", index: 0 }]);
        }
      } else {
        const existingIdx = sortConfig.findIndex((s) => s.key === columnKey);
        if (existingIdx >= 0) {
          const existing = sortConfig[existingIdx];
          if (existing.direction === "asc") {
            setSortConfig(
              sortConfig.map((s, i) =>
                i === existingIdx ? { ...s, direction: "desc" as const } : s
              )
            );
          } else {
            const remaining = sortConfig.filter((_, i) => i !== existingIdx);
            setSortConfig(remaining.map((s, i) => ({ ...s, index: i })));
          }
        } else {
          setSortConfig([
            ...sortConfig,
            { key: columnKey, direction: "asc", index: sortConfig.length },
          ]);
        }
      }
    },
    [setSortConfig, sortConfig]
  );

  const handleTotalColumnSort = useCallback(() => {
    if (!setSortConfig) return;
    const totalSort = sortConfig.find((s) => s.key === "TOTAL");
    const currentDirection = totalSort?.direction;
    const newDirection =
      !currentDirection || currentDirection === "desc" ? "asc" : "desc";
    setSortConfig([{ key: "TOTAL", direction: newDirection, index: 0 }]);
  }, [setSortConfig, sortConfig]);

  // ---------------------------------------------------------------------------
  // Sort-key sidecar: fetch a small parquet from the backend containing one
  // row per segmentation for the selected measure, then sort locally.  This
  // avoids loading ALL chunks of the main matrix parquet into browser memory.
  // ---------------------------------------------------------------------------

  const [sortKeyArrowTable, setSortKeyArrowTable] = useState<arrow.Table | null>(null);
  const [sortKeyMeasure, setSortKeyMeasure] = useState<string | null>(null);
  const sortKeyLoadingRef = useRef<string | null>(null);
  const prevMeasureRef = useRef<string | null>(null);

  // Fetch sort-key data from the backend when sort is active.
  // Wait until totalSegmentations > 0 (transformation has completed and data is
  // flowing) so the backend's S3 cache has the parquet URL available.
  const [sortKeyRetryCount, setSortKeyRetryCount] = useState(0);
  useEffect(() => {
    if (viewParams.measure && viewParams.measure !== prevMeasureRef.current) {
      prevMeasureRef.current = viewParams.measure;
      setSortKeyRetryCount(0);
    }
  }, [viewParams.measure]);

  useEffect(() => {
    if (!fetchSortKeys || sortConfig.length === 0 || !viewParams.measure) return;
    if (totalSegmentations === 0) return;
    if (sortKeyMeasure === viewParams.measure) return;
    if (sortKeyLoadingRef.current === viewParams.measure) return;
    if (sortKeyRetryCount >= 3) return;

    sortKeyLoadingRef.current = viewParams.measure;
    console.log(
      `[sort keys] Fetching from backend for measure: ${viewParams.measure} ` +
      `(totalSegs=${totalSegmentations}, retry=${sortKeyRetryCount})`
    );

    fetchSortKeys(viewParams.measure)
      .then((result) => {
        if (sortKeyLoadingRef.current !== viewParams.measure) return;
        sortKeyLoadingRef.current = null;
        if (result) {
          setSortKeyRetryCount(0);
          setSortKeyArrowTable(result.arrowTable);
          setSortKeyMeasure(viewParams.measure);
          console.log(
            `[sort keys] Loaded for measure: ${viewParams.measure}, rows: ${result.arrowTable.numRows}`
          );
        } else {
          console.warn(
            `[sort keys] Backend returned null for measure: ${viewParams.measure} ` +
            `(retry ${sortKeyRetryCount + 1}/3)`
          );
          setTimeout(() => setSortKeyRetryCount((c) => c + 1), 2000);
        }
      })
      .catch((err) => {
        sortKeyLoadingRef.current = null;
        console.error("[sort keys] Backend fetch failed:", err);
        setTimeout(() => setSortKeyRetryCount((c) => c + 1), 2000);
      });
  }, [fetchSortKeys, sortConfig.length, viewParams.measure, sortKeyMeasure, totalSegmentations, sortKeyRetryCount]);

  // Build sortedSegIndices from the fetched sort-key Arrow table.
  const { sortedSegIndices, isSortReady } = useMemo(() => {
    const identity = Array.from({ length: totalSegmentations }, (_, i) => i);

    if (
      sortConfig.length === 0 ||
      totalSegmentations === 0 ||
      M === 0 ||
      !sortKeyArrowTable ||
      sortKeyArrowTable.numRows === 0
    ) {
      return {
        sortedSegIndices: identity,
        isSortReady: sortConfig.length === 0 && defaultSortInitialized,
      };
    }

    const skTable = sortKeyArrowTable;
    const skFixedCols = new Set([
      "segmentation",
      "measure",
      "measure_rank",
      "segmentation_rank",
      "TOTAL",
      // Segment dimension columns (e.g. sku, location) vary per dataset;
      // exclude any non-numeric column name since date columns are timestamps.
    ]);
    const isDateColumnName = (name: string) => {
      const n = parseInt(name);
      return !isNaN(n) && n > 1000000000000;
    };
    const skDateCols = skTable.schema.fields
      .filter((f) => !skFixedCols.has(f.name) && isDateColumnName(f.name))
      .map((f) => f.name);
    // Segment dimension columns in the sidecar (e.g. sku, location)
    const skSegCols = skTable.schema.fields
      .filter((f) => !skFixedCols.has(f.name) && !isDateColumnName(f.name))
      .map((f) => f.name);

    type SortKeyEntry = { dateValues: Record<string, number>; total: number; segValues: Record<string, string> };
    const cache = new Map<number, SortKeyEntry>();
    const totalVec = skTable.getChild("TOTAL");
    for (let i = 0; i < skTable.numRows && i < totalSegmentations; i++) {
      const dateValues: Record<string, number> = {};
      for (const col of skDateCols) {
        const v = skTable.getChild(col)?.get(i);
        dateValues[col] =
          typeof v === "number"
            ? v
            : typeof v === "string"
              ? parseFloat(v.replace(/,/g, "")) || 0
              : Number(v) || 0;
      }
      const segValues: Record<string, string> = {};
      for (const col of skSegCols) {
        const v = skTable.getChild(col)?.get(i);
        segValues[col] = String(v ?? "");
      }
      const rawTotal = totalVec?.get(i) ?? 0;
      const total =
        typeof rawTotal === "number"
          ? rawTotal
          : typeof rawTotal === "string"
            ? parseFloat(String(rawTotal).replace(/,/g, "")) || 0
            : Number(rawTotal) || 0;
      cache.set(i, { dateValues, total, segValues });
    }

    const sorted = [...identity];
    sorted.sort((aIdx, bIdx) => {
      const a = cache.get(aIdx);
      const b = cache.get(bIdx);
      if (!a || !b) return 0;

      const totalSort = sortConfig.find((s) => s.key === "TOTAL");
      if (totalSort) {
        const comparison = a.total - b.total;
        if (comparison !== 0) {
          return totalSort.direction === "desc" ? -comparison : comparison;
        }
        return 0;
      }

      for (const sort of sortConfig) {
        const dateValue = parseInt(sort.key);
        if (!isNaN(dateValue) && dateValue > 1000000000000) {
          // Date column sort
          const aNum = a.dateValues[sort.key] ?? 0;
          const bNum = b.dateValues[sort.key] ?? 0;
          const comparison = aNum - bNum;
          if (comparison !== 0) {
            return sort.direction === "desc" ? -comparison : comparison;
          }
        } else if (sort.key in a.segValues) {
          // Segment dimension column sort (string comparison)
          const aVal = a.segValues[sort.key] ?? "";
          const bVal = b.segValues[sort.key] ?? "";
          const comparison = aVal.localeCompare(bVal);
          if (comparison !== 0) {
            return sort.direction === "desc" ? -comparison : comparison;
          }
        }
      }

      return 0;
    });

    return { sortedSegIndices: sorted, isSortReady: true };
  }, [sortConfig, totalSegmentations, M, sortKeyArrowTable, defaultSortInitialized]);

  const getRow = useCallback(
    (virtualIndex: number): ChunkedMatrixRowData => {
      if (virtualIndex < 0 || virtualIndex >= virtualRowCount)
        return { type: "detail", dateValues: {}, segmentationRank: 0 };

      let cumulative = 0;
      for (let sortedIdx = 0; sortedIdx < totalSegmentations; sortedIdx++) {
        const segIdx = sortedSegIndices[sortedIdx];
        const segKey = getSegKey(segIdx);
        const groupKey = segKey || `__seg_${segIdx}`;
        const rowCountForSeg =
          1 +
          (expandedGroups.has("*") || expandedGroups.has(groupKey) ? M : 0);
        if (cumulative + rowCountForSeg > virtualIndex) {
          const rowInBlock = virtualIndex - cumulative;
          const block = getBlock(segIdx);
          if (!matrixDebugMeasuresLogged.current && segIdx === 0 && !block.some(isLoadingRow)) {
            matrixDebugMeasuresLogged.current = true;
            const measureNames = block.map((r) => String(r.measure ?? r.measure_name ?? ""));
            console.log(
              "[matrix chunk debug] First block measure names (if 'Starting weeks/months of supply' missing, transform must output them):",
              measureNames
            );
          }
          if (block.some(isLoadingRow)) {
            const parquetStart = segIdx * M;
            const parquetEnd = segIdx * M + M - 1;
            if (segIdx <= 2 || segIdx % 100 === 0) {
              console.log("[matrix chunk debug] Grey row: segment block still loading", {
                virtualIndex,
                segIdx,
                parquetRowRange: [parquetStart, parquetEnd],
                M,
              });
            }
            return { type: "detail", dateValues: {}, segmentationRank: 0 };
          }

          const segRank = Number(block[0].segmentation_rank ?? 0);

          if (rowInBlock === 0) {
            const groupRow = block.find(
              (r) => Number(r.measure_rank) === selectedMeasureRank
            ) ?? block[0];
            const dateValues: Record<string, unknown> = {};
            const frozenDates: Record<string, string> = {};
            dateColumnKeys.forEach((col) => {
              dateValues[col] = groupRow[col] ?? groupRow[Number(col)];
              const fd = groupRow[`${col}__frozen_date`];
              if (fd != null && fd !== "") frozenDates[col] = String(fd);
            });
            // Use unique key when segmentation is empty so expand/collapse is per-row
            const groupKey = segKey || `__seg_${segIdx}`;
            const rowData: ChunkedMatrixRowData = {
              type: "group",
              groupKey,
              dateValues,
              segmentationRank: segRank,
              isExpanded:
                expandedGroups.has("*") || expandedGroups.has(groupKey),
              groupSize: M,
              segmentation: segKey,
              segIdx,
              frozenDates,
            };
            // Add individual segment dimension values for separate column rendering
            for (const col of segmentDimensionColumns) {
              rowData[col] = groupRow[col] ?? "";
            }
            allDates.forEach((d) => {
              const dateKey = `${d.valueOf()}`;
              rowData[dateKey] =
                dateValues[dateKey] ?? dateValues[Number(dateKey)] ?? "";
            });
            if (shouldShowTotals && hasTotalColumn)
              rowData["TOTAL"] =
                groupRow["TOTAL"] ?? groupRow["total"] ?? 0;
            if (!matrixDebugFirstRowLogged.current && segIdx === 0) {
              matrixDebugFirstRowLogged.current = true;
              console.log("[matrix transform debug] First group row built:", {
                segIdx: 0,
                segKey,
                groupKey: rowData.groupKey,
                segmentation: rowData.segmentation,
                isExpanded: rowData.isExpanded,
                M,
                totalSegmentations,
              });
            }
            return rowData;
          }

          // Show all M measures in expanded section (including the selected one) so
          // e.g. "starting weeks of supply" appears when expanded
          const detailMeasureRank = measureRanksInOrder[rowInBlock - 1];
          const detailRow = block.find(
            (r) => Number(r.measure_rank) === detailMeasureRank
          ) ?? block[rowInBlock];
          const dateValues: Record<string, unknown> = {};
          const frozenDates: Record<string, string> = {};
          dateColumnKeys.forEach((col) => {
            dateValues[col] = detailRow[col] ?? detailRow[Number(col)];
            const fd = detailRow[`${col}__frozen_date`];
            if (fd != null && fd !== "") frozenDates[col] = String(fd);
          });
          const groupKey = segKey || `__seg_${segIdx}`;
          const measureDisplay =
            detailRow.measure ??
            detailRow.measure_name ??
            (detailRow as Record<string, unknown>)["Measure"];
          const rowData: ChunkedMatrixRowData = {
            type: "detail",
            groupKey,
            measure: String(measureDisplay ?? ""),
            dateValues,
            segmentationRank: segRank,
            measureRank: detailMeasureRank,
            parentSegmentation: segKey,
            segmentation: String(detailRow.measure ?? ""),
            segIdx,
            detailIndex: rowInBlock - 1,
            frozenDates,
          };
          // Add individual segment dimension values for separate column rendering
          for (const col of segmentDimensionColumns) {
            rowData[col] = detailRow[col] ?? "";
          }
          allDates.forEach((d) => {
            const dateKey = `${d.valueOf()}`;
            rowData[dateKey] =
              dateValues[dateKey] ?? dateValues[Number(dateKey)] ?? "";
          });
          if (shouldShowTotals && hasTotalColumn)
            rowData["TOTAL"] =
              detailRow["TOTAL"] ?? detailRow["total"] ?? 0;
          return rowData;
        }
        cumulative += rowCountForSeg;
      }
      return { type: "detail", dateValues: {}, segmentationRank: 0 };
    },
    [
      virtualRowCount,
      totalSegmentations,
      sortedSegIndices,
      getSegKey,
      getBlock,
      expandedGroups,
      M,
      selectedMeasureRank,
      measureRanksInOrder,
      dateColumnKeys,
      allDates,
      shouldShowTotals,
      hasTotalColumn,
      segmentDimensionColumns,
    ]
  );

  const muiColumns = useMemo(() => {
    // Actuals/forecast split at the run's as-of date (strip feature).
    const actualsKeys = stripEnabled
      ? allDates
          .filter((d) => isHistoricalDate(d.valueOf(), runAsOf ?? undefined))
          .map((d) => `${d.valueOf()}`)
      : [];
    const actualsKeySet = new Set(actualsKeys);
    const lastActualsKey =
      actualsKeys.length > 0 ? actualsKeys[actualsKeys.length - 1] : null;
    const collapsedActuals =
      stripEnabled && actualsCollapsed && actualsKeys.length > 0;
    const visibleDates = collapsedActuals
      ? allDates.filter((d) => !actualsKeySet.has(`${d.valueOf()}`))
      : allDates;

    // Single summary column standing in for the collapsed actuals columns:
    // sums the actuals values for summable measures, em-dash for rates/WOS.
    const actualsSummaryCol: ChunkedMatrixTableDataResult["columns"][number] = {
      key: "__actuals_summary",
      label: "ACTUALS Σ",
      width: 120,
      sortable: false,
      boundaryRight: true,
      renderCell: (row: ChunkedMatrixRowData) => {
        const effectiveMeasure =
          (row.type === "detail" ? row.measure : viewParams?.measure) ?? "";
        const m = effectiveMeasure.toLowerCase();
        const summable = !(m.includes("of supply") || m.includes("rate"));
        let content: string = "—";
        if (summable) {
          let sum = 0;
          let any = false;
          actualsKeys.forEach((k) => {
            const v = row.dateValues[k] ?? row.dateValues[Number(k)];
            const n = Number(String(v ?? "").replace(/,/g, ""));
            if (v != null && v !== "" && !isNaN(n)) {
              sum += n;
              any = true;
            }
          });
          content = any ? formatMatrixCellValue(sum, effectiveMeasure) : "";
        }
        return React.createElement(
          Box,
          {
            sx: {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              backgroundColor: "#F4F6F8",
              color: content === "—" ? "#B8BDC4" : undefined,
              p: 0,
            },
          },
          content
        );
      },
    };

    const cols: ChunkedMatrixTableDataResult["columns"] = [
      {
        key: "expand",
        label: "",
        width: 40,
        isExpandColumn: true,
        sortable: false,
        sticky: true,
        renderCell: (row: ChunkedMatrixRowData) => {
          if (row.type !== "group") return null;
          return React.createElement(Box, {
            sx: {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
            },
            children: React.createElement(IconButton, {
              size: "small",
              onClick: () =>
                setExpandedGroups((prev) => {
                  const next = new Set(prev);
                  if (row.groupKey) {
                    if (next.has(row.groupKey)) next.delete(row.groupKey);
                    else next.add(row.groupKey);
                  }
                  return next;
                }),
              sx: { p: 0 },
              children: row.isExpanded
                ? React.createElement(ExpandLessIcon)
                : React.createElement(ExpandMoreIcon),
            }),
          });
        },
      },
      ...(orderedSegmentColumns.length > 0
        ? orderedSegmentColumns.map((field, fieldIdx) => ({
            key: field,
            label: field,
            width: 160,
            sortable: true,
            sticky: true,
            onSort: (e?: React.MouseEvent<HTMLTableCellElement>) =>
              handleSegmentColumnSort(field, e?.shiftKey ?? false),
            renderCell: (row: ChunkedMatrixRowData) => {
              const isGroup = row.type === "group";
              const isDetail = row.type === "detail";
              const allBreakdownMeasures = breakdownMeasures
                ? [...["open", "mods", "simulated", "raw purchase need"], ...breakdownMeasures]
                : ["open", "mods", "simulated", "raw purchase need"];
              const isBreakdownRow =
                isDetail &&
                fieldIdx === 0 &&
                row.measure &&
                allBreakdownMeasures.some(
                  (s) => row.measure?.toLowerCase() === s
                );
              return React.createElement(Box, {
                sx: {
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  fontWeight: isGroup ? "bold" : "normal",
                  // plainDetailLabels keeps the label column white so the
                  // row-hover highlight reads; other views zebra-stripe the
                  // expanded detail rows.
                  backgroundColor: plainDetailLabels
                    ? undefined
                    : isDetail
                      ? detailZebraColor(row)
                      : undefined,
                  paddingLeft: isBreakdownRow ? 4 : isDetail && fieldIdx === 0 ? 2 : 1,
                },
                // Full value on hover, since the label is the column most
                // likely to be dragged narrow.
                title: isGroup ? String(row[field] ?? "") : undefined,
                // The flex parent handles alignment/background; the text needs
                // its own block to ellipsize against.
                children: React.createElement(Box, {
                  sx: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  },
                  children: isGroup
                    ? String(row[field] ?? "")
                    : (fieldIdx === 0 ? formatMeasureLabel(row.measure) : ""),
                }),
              });
            },
          }))
        : [
            {
              key: "segmentation",
              label: "segmentation / measure",
              width: 200,
              sortable: false,
              sticky: true,
              renderCell: (row: ChunkedMatrixRowData) => {
                const isGroup = row.type === "group";
                const isDetail = row.type === "detail";
                const allBreakdownMeasures = breakdownMeasures
                  ? [...["open", "mods", "simulated", "raw purchase need"], ...breakdownMeasures]
                  : ["open", "mods", "simulated", "raw purchase need"];
                const isBreakdownRow =
                  isDetail &&
                  row.measure &&
                  allBreakdownMeasures.some(
                    (s) => row.measure?.toLowerCase() === s
                  );
                return React.createElement(Box, {
                  sx: {
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    fontWeight: isGroup ? "bold" : "normal",
                    // plainDetailLabels keeps the label column white; other
                    // views zebra-stripe the expanded detail rows.
                    backgroundColor: plainDetailLabels
                      ? undefined
                      : isDetail
                        ? detailZebraColor(row)
                        : undefined,
                    paddingLeft: isBreakdownRow ? 4 : isDetail ? 2 : 1,
                  },
                  title: isGroup ? String(row.segmentation ?? "") : undefined,
                  children: React.createElement(Box, {
                    sx: {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    },
                    children: isGroup
                      ? (row.segmentation ?? "")
                      : formatMeasureLabel(row.measure),
                  }),
                });
              },
            },
          ]),
      ...(collapsedActuals ? [actualsSummaryCol] : []),
      ...visibleDates.map((date) => ({
        key: `${date.valueOf()}`,
        width: 120,
        sortable: true,
        onSort: (e?: React.MouseEvent<HTMLTableCellElement>) => handleDateColumnSort(`${date.valueOf()}`, e?.shiftKey ?? false),
        headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
        boundaryRight:
          !collapsedActuals && lastActualsKey === `${date.valueOf()}`,
        renderCell: (row: ChunkedMatrixRowData) => {
          const dateValue = date.valueOf();
          const isActualsCol = actualsKeySet.has(`${dateValue}`);
          const value = row.dateValues[`${dateValue}`] ?? row.dateValues[dateValue];
          const numericString = String(value ?? "").replace(/,/g, "");
          let cellColor: string | undefined;
          let isWOS = false;
          // Plain "<period>s of supply" is the inventory_wos_cora measure name
          // (no starting/ending prefix). Exact equality keeps the target row
          // ("target weeks of supply") uncolored.
          const plainWOSKey = `${getPeriodLabel(
            viewParams?.dateHierarchy || ""
          ).toLowerCase()} of supply`;
          if (row.type === "detail" && row.measure) {
            const m = row.measure.toLowerCase();
            isWOS =
              m === getWOSMeasureKey("starting", viewParams?.dateHierarchy || "").toLowerCase() ||
              m === getWOSMeasureKey("ending", viewParams?.dateHierarchy || "").toLowerCase() ||
              m === plainWOSKey;
          } else if (row.type === "group") {
            const measureLower = (viewParams?.measure || "").toLowerCase();
            isWOS =
              (measureLower.includes("starting") && measureLower.includes("supply")) ||
              (measureLower.includes("ending") && measureLower.includes("supply")) ||
              measureLower === plainWOSKey;
          }
          if (isWOS && numericString !== "" && !isNaN(Number(numericString))) {
            const currentValue = Number(numericString);
            const segIdx =
              typeof row.segIdx === "number" ? row.segIdx : -1;
            const block = segIdx >= 0 ? getBlock(segIdx) : [];
            let targetWOS: number | null = null;
            for (const r of block) {
              if (
                String(r.measure ?? "").toLowerCase().includes("target") &&
                String(r.measure ?? "").toLowerCase().includes("supply")
              ) {
                const v =
                  r[`${dateValue}`] ?? r[dateValue] ?? r[Number(dateValue)];
                if (v != null && v !== "") {
                  const parsed = Number(String(v).replace(/,/g, ""));
                  if (!isNaN(parsed)) targetWOS = parsed;
                }
                break;
              }
            }
            if (targetWOS != null) {
              cellColor = getSupplyCellBackgroundColor({
                value: currentValue,
                targetValue: targetWOS,
                theme: theme as any,
              });
            }
          }
          const effectiveMeasure =
            (row.type === "detail" ? row.measure : viewParams?.measure) ?? undefined;
          const displayValue = formatMatrixCellValue(value, effectiveMeasure);
          // Frozen buy/demand cells carry a faint corner lock and a native
          // hover tooltip showing the freeze date; text color matches live
          // cells (the lock alone marks frozen).
          const measureLower = (effectiveMeasure ?? "").toLowerCase();
          const frozenDate =
            measureLower === "buy_forecast" || measureLower === "demand_forecast"
              ? row.frozenDates?.[`${dateValue}`]
              : undefined;
          // Demand/supply-style shading (forecast tracking), gated by
          // shadeHistory so inventory-health views are unaffected: grey
          // historical (past/current) month columns for every row.
          // Inventory-health precedence: WOS band tint > actuals column tint
          // > detail-row zebra.
          const backgroundColor = shadeHistory
            ? isHistoricalDate(dateValue)
              ? "action.hover"
              : undefined
            : cellColor ||
              (isActualsCol ? "#F4F6F8" : undefined) ||
              (row.type === "detail" ? detailZebraColor(row) : undefined);
          // Text color: dim true zeros so real movement stands out; signed /
          // rate measures color the number itself (no extra fills).
          const numericValue =
            numericString !== "" && !isNaN(Number(numericString))
              ? Number(numericString)
              : null;
          const effectiveMeasureLower = (
            (row.type === "detail" ? row.measure : viewParams?.measure) ?? ""
          ).toLowerCase();
          let textColor: string | undefined;
          if (numericValue === 0 && !isWOS) {
            textColor = "#B8BDC4";
          } else if (
            numericValue != null &&
            ((SIGNED_SHORTFALL_MEASURES.has(effectiveMeasureLower) &&
              numericValue > 0) ||
              (effectiveMeasureLower === "in stock rate" && numericValue < 1))
          ) {
            textColor = "#c0392b";
          }
          // PO popover: every cell traceable to purchase orders gets the
          // hover affordance — the "purchase order arrivals" measure itself
          // (kind "all"), its open/simulated breakdown rows, and group rows
          // when PO arrivals is the active measure. "open"/"simulated" names
          // are ambiguous (transfer order arrivals reuses them), so breakdown
          // rows resolve their PARENT measure from the block by rank. The
          // click goes through the module-level store, NOT React state —
          // opening must not re-render the table (see po_cell_popover/store).
          let poCellKind: "open" | "simulated" | "all" | undefined;
          if (enablePoCellPopover === true) {
            if (effectiveMeasureLower === "purchase order arrivals") {
              poCellKind = "all";
            } else if (
              row.type === "detail" &&
              (effectiveMeasureLower === "open" ||
                effectiveMeasureLower === "simulated") &&
              typeof row.measureRank === "number" &&
              typeof row.segIdx === "number"
            ) {
              const breakdownSet = new Set([
                "open",
                "mods",
                "simulated",
                "raw purchase need",
                ...(breakdownMeasures ?? []).map((m) => m.toLowerCase()),
              ]);
              let parentName: string | undefined;
              let parentRank = -Infinity;
              for (const r of getBlock(row.segIdx)) {
                const rank = Number(r.measure_rank);
                const name = String(r.measure ?? "").toLowerCase();
                if (
                  !isNaN(rank) &&
                  rank < row.measureRank &&
                  rank > parentRank &&
                  !breakdownSet.has(name)
                ) {
                  parentRank = rank;
                  parentName = name;
                }
              }
              if (parentName === "purchase order arrivals") {
                poCellKind = effectiveMeasureLower as "open" | "simulated";
              }
            }
          }
          const isPoPopoverCell =
            poCellKind != null &&
            numericValue != null &&
            numericValue > 0 &&
            Boolean(row["sku"]) &&
            Boolean(row["location"]);
          const brandBlue = (theme as any)?.palette?.atomic?.brandBlue ?? "#3877F7";
          const handlePoLogoClick = isPoPopoverCell
            ? (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                openPoCellPopover({
                  // Anchor the popover (and its outline) to the cell, not the button
                  anchorEl:
                    (e.currentTarget.parentElement as HTMLElement) ??
                    e.currentTarget,
                  sku: String(row["sku"]),
                  location: String(row["location"]),
                  periodStartMs: dateValue,
                  kind: poCellKind!,
                });
              }
            : undefined;
          return React.createElement(
            Box,
            {
              title: frozenDate ? `frozen ${frozenDate}` : undefined,
              sx: {
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                backgroundColor,
                color: textColor,
                p: 0,
                ...(isPoPopoverCell
                  ? {
                      "&:hover": {
                        boxShadow: `inset 0 0 0 2px ${brandBlue}`,
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                      },
                      "&:hover .poCellLogoBtn": {
                        opacity: 1,
                        pointerEvents: "auto",
                      },
                    }
                  : {}),
              },
            },
            displayValue,
            frozenDate
              ? React.createElement(LockOutlinedIcon, {
                  sx: {
                    position: "absolute",
                    top: 1,
                    right: 1,
                    fontSize: 9,
                    color: "text.disabled",
                    opacity: 0.5,
                    pointerEvents: "none",
                  },
                })
              : null,
            handlePoLogoClick
              ? React.createElement(
                  IconButton,
                  {
                    className: "poCellLogoBtn",
                    "aria-label": "View purchase orders",
                    onClick: handlePoLogoClick,
                    sx: {
                      position: "absolute",
                      left: 2,
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      padding: 0,
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      "&:hover": { backgroundColor: "#fff" },
                      // Hidden until the cell is hovered (CSS-only reveal —
                      // no React state, so hovering repaints zero cells).
                      opacity: 0,
                      pointerEvents: "none",
                      transition: "opacity 80ms linear",
                    },
                  },
                  React.createElement(AtomicLogo, { width: 14, height: 14 })
                )
              : null,
          );
        },
      })),
    ];
    if (shouldShowTotals && hasTotalColumn) {
      cols.push({
        key: "TOTAL",
        width: 120,
        sortable: true,
        onSort: handleTotalColumnSort,
        renderCell: (row: ChunkedMatrixRowData) => {
          const total = row["TOTAL"];
          const formatted =
            typeof total === "number"
              ? total.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })
              : String(total ?? "");
          return React.createElement(Box, {
            sx: {
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              // TOTAL column is a sum across all months (not historical) → white
              // when shadeHistory; inventory-health keeps its detail shading.
              backgroundColor: shadeHistory
                ? undefined
                : row.type === "detail"
                  ? "action.hover"
                  : undefined,
              p: 0,
              fontWeight: row.type === "group" ? "bold" : "normal",
            },
            children: formatted,
          });
        },
      });
    }
    return cols;
  }, [
    allDates,
    viewParams,
    setExpandedGroups,
    theme,
    shouldShowTotals,
    hasTotalColumn,
    getBlock,
    handleDateColumnSort,
    handleTotalColumnSort,
    handleSegmentColumnSort,
    segmentDimensionColumns,
    orderedSegmentColumns,
    breakdownMeasures,
    shadeHistory,
    enablePoCellPopover,
    plainDetailLabels,
    stripEnabled,
    runAsOf,
    actualsCollapsed,
  ]);

  // ❯ ACTUALS / FORECAST strip config for MUIFlatTable. undefined when the
  // feature is off or the visible range has no actuals columns.
  const headerBand = useMemo(():
    | ChunkedMatrixTableDataResult["headerBand"]
    | undefined => {
    if (!stripEnabled) return undefined;
    const actualsCount = allDates.filter((d) =>
      isHistoricalDate(d.valueOf(), runAsOf ?? undefined)
    ).length;
    if (actualsCount === 0) return undefined;
    const forecastCount = allDates.length - actualsCount;
    const dateColsShown = actualsCollapsed ? 1 + forecastCount : allDates.length;
    const trailingColumnCount = shouldShowTotals && hasTotalColumn ? 1 : 0;
    const leadColumnCount = Math.max(
      muiColumns.length - dateColsShown - trailingColumnCount,
      0
    );
    const stickyLeftWidth = muiColumns
      .slice(0, leadColumnCount)
      .reduce(
        (acc, c) => acc + ((c as { sticky?: boolean }).sticky ? Number(c.width) || 0 : 0),
        0
      );
    return {
      leadColumnCount,
      actualsColumnCount: actualsCollapsed ? 1 : actualsCount,
      forecastColumnCount: forecastCount,
      trailingColumnCount,
      collapsed: actualsCollapsed,
      onToggleCollapsed: () => setActualsCollapsed((v) => !v),
      stickyLeftWidth,
    };
  }, [
    stripEnabled,
    runAsOf,
    actualsCollapsed,
    allDates,
    muiColumns,
    shouldShowTotals,
    hasTotalColumn,
  ]);

  return {
    columns: muiColumns,
    getRow,
    rowCount: virtualRowCount,
    columnTotals: undefined,
    isReady: M > 0 && totalSegmentations > 0,
    isSortReady,
    headerBand,
  };
}

/** Transform IDs that produce matrix-shaped parquet (chunked path). */
export const CHUNKED_MATRIX_TRANSFORM_IDS = new Set([
  "supply_walk_parallelized_matrix",
  "supply_walk_parallelized_matrix_with_transfers",
  "supply_walk_parallelized_matrix_for_hubs",
  "raw_inventory_health_matrix",
  "forecast_tracking_matrix",
  "three_forecast_tracking_matrix",
  "transfer_recommendations_matrix",
  "transfer_recommendations_matrix_hub_to_spoke",
  "inventory_wos_cora_matrix",
]);

/** Loading state component for chunked matrix (while segmentation keys load). */
function ChunkedMatrixLoadingState() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      Loading table…
    </Box>
  );
}

export type ChunkedMatrixTableViewerProps = {
  chunkedData: ChunkedParsedParquetData;
  viewParams: DatePivotParams;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnHeaderMap?: Record<string, string>;
  theme: unknown;
  shadeHistory?: boolean;
  showHeaderRow?: boolean;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  TableComponent: React.ComponentType<{
    columns: any[];
    getRow: (i: number) => any;
    rowCount: number;
    columnHeaderMap?: Record<string, string>;
    sortConfig?: any[];
    columnTotals?: Record<string, number>;
    expandedGroups?: Set<string>;
    onToggleGroup?: (groupKey: string) => void;
    isHeaderExpanded?: boolean;
    onToggleHeader?: () => void;
  }>;
};

/**
 * Viewer that uses chunked matrix table data (hook) and renders the table when ready.
 * Use this when parsedData is chunked and transformId is a CHUNKED_MATRIX_TRANSFORM_ID.
 */
export function ChunkedMatrixTableViewer({
  chunkedData,
  viewParams,
  expandedGroups,
  setExpandedGroups,
  sortConfig,
  setSortConfig,
  columnHeaderMap,
  theme,
  shadeHistory,
  showHeaderRow,
  fetchSortKeys,
  TableComponent,
}: ChunkedMatrixTableViewerProps) {
  const tableData = useChunkedMatrixTableData({
    chunkedData,
    viewParams,
    expandedGroups,
    setExpandedGroups,
    theme,
    sortConfig,
    setSortConfig,
    fetchSortKeys,
    shadeHistory,
  });

  if (!tableData.isReady || tableData.rowCount === 0) {
    return <ChunkedMatrixLoadingState />;
  }

  return (
    <TableComponent
      columns={tableData.columns}
      getRow={tableData.getRow}
      rowCount={tableData.rowCount}
      columnHeaderMap={columnHeaderMap}
      sortConfig={sortConfig}
      columnTotals={tableData.columnTotals}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(groupKey)) next.delete(groupKey);
                else next.add(groupKey);
                return next;
              });
            }
          : undefined
      }
    />
  );
}
